import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  doc, 
  setDoc, 
  getDoc,
  getDocs,
  limit,
  where,
  updateDoc,
  increment
} from 'firebase/firestore';
import { db } from './firebase';

// Chat service for managing game-specific chat channels
export class ChatService {
  // Create or get a chat channel for a specific game
  static async getOrCreateGameChannel(gameId, gameInfo) {
    try {
      const channelRef = doc(db, 'chatChannels', gameId);
      const channelDoc = await getDoc(channelRef);
      
      if (!channelDoc.exists()) {
        // Create new channel if it doesn't exist
        await setDoc(channelRef, {
          gameId,
          homeTeam: gameInfo.homeTeam,
          awayTeam: gameInfo.awayTeam,
          sport: gameInfo.sport,
          commenceTime: gameInfo.commenceTime,
          createdAt: serverTimestamp(),
          messageCount: 0,
          lastActivity: serverTimestamp(),
          isActive: true
        });
      }
      
      return channelRef;
    } catch (error) {
      console.error('Error creating/getting game channel:', error);
      throw error;
    }
  }

  // Send a message to a specific game channel
  static async sendMessage(gameId, userId, userName, message, userAvatar = null) {
    try {
      if (!message.trim()) {
        throw new Error('Message cannot be empty');
      }

      const messagesRef = collection(db, 'chatChannels', gameId, 'messages');
      const channelRef = doc(db, 'chatChannels', gameId);
      
      // Add the message
      const messageData = {
        userId,
        userName,
        userAvatar,
        message: message.trim(),
        timestamp: serverTimestamp(),
        edited: false,
        likes: 0,
        likedBy: []
      };

      const messageDoc = await addDoc(messagesRef, messageData);

      // Update channel metadata
      await updateDoc(channelRef, {
        messageCount: increment(1),
        lastActivity: serverTimestamp()
      });

      // Track user participation
      await this.addUserToChannel(gameId, userId, userName);
      await this.updateUserActivity(gameId, userId);

      // Update last message for all participants
      await this.updateLastMessage(gameId, {
        ...messageData,
        id: messageDoc.id
      });

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Subscribe to messages in a game channel
  static subscribeToMessages(gameId, callback, messageLimit = 50) {
    try {
      const messagesRef = collection(db, 'chatChannels', gameId, 'messages');
      const q = query(
        messagesRef, 
        orderBy('timestamp', 'desc'), 
        limit(messageLimit)
      );

      return onSnapshot(q, (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
          messages.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Reverse to show oldest first
        callback(messages.reverse());
      }, (error) => {
        console.error('Error subscribing to messages:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up message subscription:', error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  // Get active chat channels (for general chat screen)
  static subscribeToActiveChannels(callback, channelLimit = 20) {
    try {
      const channelsRef = collection(db, 'chatChannels');
      const q = query(
        channelsRef, 
        where('isActive', '==', true),
        orderBy('lastActivity', 'desc'), 
        limit(channelLimit)
      );

      return onSnapshot(q, (snapshot) => {
        const channels = [];
        snapshot.forEach((doc) => {
          channels.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        callback(channels);
      }, (error) => {
        console.error('Error subscribing to channels:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up channels subscription:', error);
      return () => {}; // Return empty unsubscribe function
    }
  }

  // Like/unlike a message
  static async toggleMessageLike(gameId, messageId, userId) {
    try {
      const messageRef = doc(db, 'chatChannels', gameId, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();
      const likedBy = messageData.likedBy || [];
      const hasLiked = likedBy.includes(userId);

      if (hasLiked) {
        // Remove like
        await updateDoc(messageRef, {
          likes: increment(-1),
          likedBy: likedBy.filter(id => id !== userId)
        });
      } else {
        // Add like
        await updateDoc(messageRef, {
          likes: increment(1),
          likedBy: [...likedBy, userId]
        });
      }

      return !hasLiked; // Return new like state
    } catch (error) {
      console.error('Error toggling message like:', error);
      throw error;
    }
  }

  // Track user participation in a chat channel
  static async addUserToChannel(gameId, userId, userName) {
    try {
      const userRef = doc(db, 'chatChannels', gameId, 'participants', userId);
      await setDoc(userRef, {
        userId,
        userName,
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        messageCount: 0
      }, { merge: true });

      // Also track in user's personal chat list
      const userChatRef = doc(db, 'userChats', userId, 'channels', gameId);
      await setDoc(userChatRef, {
        gameId,
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        lastMessage: null,
        unreadCount: 0
      }, { merge: true });

      return true;
    } catch (error) {
      console.error('Error adding user to channel:', error);
      throw error;
    }
  }

  // Update user's last seen and message count
  static async updateUserActivity(gameId, userId) {
    try {
      const userRef = doc(db, 'chatChannels', gameId, 'participants', userId);
      const userChatRef = doc(db, 'userChats', userId, 'channels', gameId);

      await Promise.all([
        updateDoc(userRef, {
          lastSeen: serverTimestamp(),
          messageCount: increment(1)
        }),
        updateDoc(userChatRef, {
          lastSeen: serverTimestamp(),
          unreadCount: 0 // Reset unread count when user sends message
        })
      ]);
    } catch (error) {
      console.error('Error updating user activity:', error);
      // Don't throw error as this is not critical
    }
  }

  // Get user's participated chat channels
  static subscribeToUserChats(userId, callback) {
    try {
      const userChatsRef = collection(db, 'userChats', userId, 'channels');
      const q = query(
        userChatsRef,
        orderBy('lastSeen', 'desc')
      );

      return onSnapshot(q, async (snapshot) => {
        const userChannels = [];
        const channelPromises = [];

        snapshot.forEach((docSnapshot) => {
          const channelData = docSnapshot.data();
          const gameId = docSnapshot.id;
          
          // Fetch the actual channel info
          const channelRef = doc(db, 'chatChannels', gameId);
          channelPromises.push(
            getDoc(channelRef).then(channelDoc => {
              if (channelDoc.exists()) {
                return {
                  id: gameId,
                  ...channelDoc.data(),
                  userInfo: channelData // Include user-specific info
                };
              }
              return null;
            })
          );
        });

        try {
          const channels = await Promise.all(channelPromises);
          const validChannels = channels.filter(channel => channel !== null);
          callback(validChannels);
        } catch (error) {
          console.error('Error fetching user channels:', error);
          callback([]);
        }
      }, (error) => {
        console.error('Error subscribing to user chats:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up user chats subscription:', error);
      return () => {};
    }
  }

  // Update last message info for user chats
  static async updateLastMessage(gameId, messageData) {
    try {
      // Get all participants of this channel
      const participantsRef = collection(db, 'chatChannels', gameId, 'participants');
      const participantsSnapshot = await getDocs(participantsRef);

      const updatePromises = [];
      participantsSnapshot.forEach((participantDoc) => {
        const participantId = participantDoc.id;
        const userChatRef = doc(db, 'userChats', participantId, 'channels', gameId);
        
        // Don't increment unread count for the message sender
        const isMessageSender = participantId === messageData.userId;
        
        updatePromises.push(
          updateDoc(userChatRef, {
            lastMessage: {
              text: messageData.message,
              senderName: messageData.userName,
              timestamp: messageData.timestamp,
              senderId: messageData.userId
            },
            ...(isMessageSender ? {} : { unreadCount: increment(1) })
          })
        );
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating last message:', error);
      // Don't throw as this is not critical
    }
  }

  // Mark messages as read for a user
  static async markAsRead(gameId, userId) {
    try {
      const userChatRef = doc(db, 'userChats', userId, 'channels', gameId);
      await updateDoc(userChatRef, {
        unreadCount: 0,
        lastSeen: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking as read:', error);
      throw error;
    }
  }

  // Normalize team names to prevent ID conflicts
  static normalizeTeamName(teamName) {
    return teamName
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toLowerCase();
  }

  // Enhanced game ID generation with better consistency
  static generateGameId(game) {
    // Create a consistent ID from game data
    const gameDate = new Date(game.commence_time).toISOString().split('T')[0];
    
    // Normalize team names to prevent conflicts
    const homeTeam = this.normalizeTeamName(game.home_team);
    const awayTeam = this.normalizeTeamName(game.away_team);
    
    // Sort teams to ensure consistent ordering regardless of home/away
    const teams = [homeTeam, awayTeam].sort().join('_vs_');
    
    // Create ID with clear separators
    return `${game.sport_key}__${teams}__${gameDate}`;
  }

  // Parse game ID back to components (for reconstruction)
  static parseGameId(gameId) {
    // Try new format first
    if (gameId.includes('__')) {
      const parts = gameId.split('__');
      if (parts.length === 3) {
        const [sport_key, teamsStr, date] = parts;
        const teams = teamsStr.split('_vs_');
        
        return {
          sport_key,
          teams,
          date,
          format: 'new'
        };
      }
    }
    
    // Fallback to old format
    const parts = gameId.split('_');
    // For old format, try to guess where sport_key ends
    // Common sport keys: americanfootball_nfl, basketball_nba, etc.
    let sport_key = parts[0];
    if (parts.length > 3 && (parts[1] === 'nfl' || parts[1] === 'nba' || parts[1] === 'mlb' || parts[1] === 'nhl' || parts[1] === 'epl')) {
      sport_key = `${parts[0]}_${parts[1]}`;
    }
    
    return {
      sport_key,
      teams: [], // Can't reliably extract from old format
      date: parts[parts.length - 1],
      format: 'old'
    };
  }

  // Get game info object for channel creation
  static getGameInfo(game) {
    return {
      homeTeam: game.home_team,
      awayTeam: game.away_team,
      sport: game.sport_title,
      commenceTime: game.commence_time
    };
  }

  // Utility to find and merge duplicate channels (run manually if needed)
  static async findDuplicateChannels() {
    const channelsRef = collection(db, 'chat_channels');
    const snapshot = await getDocs(channelsRef);
    
    const channelsByGame = new Map();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const parsedId = this.parseGameId(data.gameId);
      
      // Create a normalized key for comparison
      const normalizedKey = `${parsedId.sport_key}__${data.homeTeam || 'unknown'}_vs_${data.awayTeam || 'unknown'}__${parsedId.date}`;
      
      if (channelsByGame.has(normalizedKey)) {
        channelsByGame.get(normalizedKey).push({ id: doc.id, data });
      } else {
        channelsByGame.set(normalizedKey, [{ id: doc.id, data }]);
      }
    });
    
    // Find duplicates
    const duplicates = [];
    channelsByGame.forEach((channels, key) => {
      if (channels.length > 1) {
        duplicates.push({ key, channels });
      }
    });
    
    return duplicates;
  }
}
